import React, { useEffect, useState } from 'react';
import BaseInput, { InputVariant } from '../Inputs/BaseInput';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/shared/display/Icon';
import { CloseIcon, SearchIcon } from '@/assets/Icons';

interface SearchByHeaderPanelProps {
  fieldList: { label: string; accessorKey: string }[];
  selectedHeaderLabel: string | null;
  setSelectedHeaderLabel: (label: string | null) => void;
  inputOnlyValue: string;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSuggestionClick: (suggestion: string) => void;
  handleClearInput: () => void;
  searchInputRef: React.RefObject<HTMLInputElement>;
  suggestions: string[];
  showDropdown?: boolean;
  customPlaceholder?: string;
  inputVariant?: InputVariant;
}

export const SearchByHeaderPanel = ({
  fieldList,
  selectedHeaderLabel,
  setSelectedHeaderLabel,
  inputOnlyValue,
  handleInputChange,
  handleSuggestionClick,
  handleClearInput,
  searchInputRef,
  suggestions,
  showDropdown = true,
  customPlaceholder,
  inputVariant = 'underline',
}: SearchByHeaderPanelProps) => {
  const [menuOpen, setMenuOpen] = useState(false);

  // Check if this is a global search (only "All" option or all fields are global searchable)
  const isGlobalSearchOnly = fieldList.length === 0 || (fieldList.length === 1 && fieldList[0]?.label === 'All');

  // Hide dropdown if showDropdown is false or if it's global search only
  const shouldShowDropdown = showDropdown && !isGlobalSearchOnly;

  // Get searchable field names for placeholder when dropdown is hidden
  const getSearchableFieldsPlaceholder = () => {
    // If custom placeholder is provided, use it
    if (customPlaceholder) {
      return customPlaceholder;
    }

    if (shouldShowDropdown) {
      return `Search${selectedHeaderLabel ? ` by ${selectedHeaderLabel}` : ''}...`;
    }

    // Filter out 'All' option and get field names
    const searchableFields = fieldList.filter((field) => field.label !== 'All').map((field) => field.label);

    if (searchableFields.length === 0) {
      return 'Search...';
    }

    // Create comma-separated list of fields
    const fieldsText = searchableFields.join(', ');
    return `Search by ${fieldsText}...`;
  };

  return (
    <div className="space-y-4 sticky top-0 left-0">
      <div className="flex gap-4 items-center min-w-full">
        {shouldShowDropdown && (
          <div className="flex items-center gap-2">
            <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
              <DropdownMenuTrigger asChild className="min-w-[220px]">
                <Button
                  variant="ghost"
                  className={cn(
                    'py-2 border-2 rounded-lg focus:border-primary-500 bg-white dark:bg-[#52606d] hover:bg-white dark:hover:bg-[#52606d] justify-between px-4',
                    `${menuOpen ? 'border-primary-500' : 'border-gray-200'}`
                  )}
                >
                  {'Search by: ' + (selectedHeaderLabel || 'All')}
                  <span className={`${menuOpen && 'rotate-[180deg]'}`}>
                    <ChevronDown />
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[var(--radix-popper-anchor-width)]">
                {fieldList.map(({ label }) => (
                  <DropdownMenuItem
                    key={label}
                    onClick={() => {
                      handleSuggestionClick(label);
                      setSelectedHeaderLabel(label);
                    }}
                  >
                    {label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        <div className="relative bg-white dark:bg-[#52606d] rounded-lg w-full">
          <BaseInput
            ref={searchInputRef}
            placeholder={getSearchableFieldsPlaceholder()}
            value={inputOnlyValue}
            onChange={handleInputChange}
            aria-label="Search input"
            type={'text'}
            variant={inputVariant}
            rightButton={{
              element: inputOnlyValue?.length > 0 ? <Icon icon={CloseIcon} /> : <Icon icon={SearchIcon} />,
              onClick: handleClearInput,
            }}
          />
        </div>
      </div>
    </div>
  );
};
